import { FeatureMaintenanceConfig } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { WarningTriangle } from 'iconoir-react';

interface MaintenanceOverlayProps {
  config: FeatureMaintenanceConfig;
  onClose?: () => void;
  title?: string;
  /** If true, uses absolute positioning to cover only parent container instead of fixed full-screen */
  coverContent?: boolean;
}

const defaultMessages = {
  global:
    // eslint-disable-next-line quotes
    "We're performing scheduled maintenance on the platform. Please check back later.",
  feature:
    'This feature is currently under maintenance. Please check back later.',
};

/**
 * Full-page overlay displayed when a feature or the entire app is under maintenance.
 * Shows a customizable message from the backend.
 */
export function MaintenanceOverlay({
  config,
  onClose,
  title = 'Scheduled Maintenance',
  coverContent = false,
}: MaintenanceOverlayProps) {
  const defaultMessage = onClose
    ? defaultMessages.feature
    : defaultMessages.global;

  return (
    <div
      className={`${coverContent ? 'absolute' : 'fixed'} inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm`}
    >
      <div className="mx-4 flex max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-2xl">
        {/* Icon */}
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <WarningTriangle className="h-8 w-8 text-amber-600" />
        </div>

        {/* Title */}
        <h2 className="text-center text-xl font-semibold text-gray-900">
          {title}
        </h2>

        {/* Message */}
        <p className="text-center text-gray-600">
          {config.message || defaultMessage}
        </p>

        {/* Expected end time */}
        {config.expectedEndTime && (
          <p className="text-center text-sm text-gray-500">
            Expected to be back by:{' '}
            <span className="font-medium text-gray-700">
              {config.expectedEndTime}
            </span>
          </p>
        )}

        {/* Close button - only shown for feature maintenance, not global */}
        {onClose && (
          <Button onClick={onClose} className="mt-2 w-full">
            Close
          </Button>
        )}
      </div>
    </div>
  );
}
